import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { customerApi } from '../../services/customerAuth'
import { useLanguage } from '../../context/LanguageContext'

// Lightweight read-only view of a trip via a share token. Reuses the
// shape from /my-trip/:id but renders without action buttons (no
// cancel, reschedule, share, or PDF download — that's all the
// primary's domain).

function fmtDate(d) {
  if (!d) return ''
  try { return new Date(d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) }
  catch { return String(d) }
}
function fmtShort(d) {
  if (!d) return ''
  try { return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) }
  catch { return String(d) }
}

export default function SharedTrip() {
  const { t } = useLanguage()
  const { shareToken } = useParams()
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    customerApi.get(`/shared/${shareToken}`)
      .then(({ data: res }) => setData(res.booking))
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }, [shareToken])

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>{t('sp_loading_short', 'Loading…')}</div>
  if (error) return (
    <div style={{ padding: 40, maxWidth: 480, margin: '40px auto', textAlign: 'center' }}>
      <h2 style={{ color: '#991b1b' }}>{t('sp_unable_to_load_shared_trip', 'Unable to load shared trip')}</h2>
      <p style={{ color: '#555' }}>{error}</p>
    </div>
  )
  if (!data) return null

  const b = data
  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ background: '#153230', color: '#fff', padding: '18px 20px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{t('sp_playstay_shared_trip', 'PlayStay · Shared Trip')}</div>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.15)' }}>{t('sp_view_only_caps', 'VIEW ONLY')}</span>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px 40px' }}>
        <Card>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#153230', marginBottom: 4 }}>{b.package.name}</div>
          <div style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
            {b.hostCourse.name}{b.package.isMultiCourse ? t('sp_plus_partner_courses', ' + partner courses') : ''} · {b.numGolfers} {b.numGolfers > 1 ? t('sp_golfers_lower', 'golfers') : t('sp_golfer_lower', 'golfer')}
          </div>
          <div style={{ fontSize: 14, color: '#334155' }}>{fmtDate(b.playDate)}</div>
        </Card>

        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 8 }}>{t('sp_trip_booked_by', 'Trip booked by')}</div>
          <div style={{ fontSize: 14, color: '#0f172a' }}>{b.primary.name}</div>
        </Card>

        {b.teeTimes.length > 0 && (
          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 12 }}>{t('sp_golf_label', 'Golf')}</div>
            {b.teeTimes.map((tt, i) => (
              <div key={i} style={{ padding: '10px 0', borderBottom: i === b.teeTimes.length - 1 ? 'none' : '1px solid #f1f5f9' }}>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{tt.courseName}</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>{fmtShort(tt.day)}{tt.teeTime ? ` ${t('at', 'at')} ${String(tt.teeTime).substring(0, 5)}` : ''}</div>
              </div>
            ))}
          </Card>
        )}

        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 8 }}>{t('sp_th_hotel', 'Hotel')}</div>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>{b.hotel.propertyName || t('sp_to_be_confirmed', 'To be confirmed')}</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            {t('sp_check_in_label', 'Check-in')} {fmtShort(b.hotel.checkin)} · {t('sp_check_out_label', 'Check-out')} {fmtShort(b.hotel.checkout)}
          </div>
        </Card>

        <Card>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 8 }}>{t('sp_questions', 'Questions?')}</div>
          <div style={{ fontSize: 14, color: '#334155' }}>{t('sp_contact_label', 'Contact')} {b.hostCourse.name}</div>
          {b.hostCourse.phone && <div style={{ fontSize: 14, color: '#334155', marginTop: 4 }}>{b.hostCourse.phone}</div>}
          {b.hostCourse.email && <div style={{ fontSize: 14, color: '#334155' }}>{b.hostCourse.email}</div>}
        </Card>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: '#94a3b8' }}>
          {t('sp_view_only_share_note', 'This is a view-only share. To make changes, ask')} {b.primary.name} ({t('sp_the_trip_booker', 'the trip booker')}).
        </div>
      </div>
    </div>
  )
}

function Card({ children, style }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb', marginBottom: 16, ...style }}>{children}</div>
  )
}
